let initialized = false;
type ReactGAModule = {
  initialize: (measurementId: string, options?: Record<string, unknown>) => void;
  send: (payload: Record<string, unknown>) => void;
  event: (eventName: string, params?: Record<string, string | number | boolean>) => void;
};

let reactGA: ReactGAModule | null = null;

function getReactGA(): ReactGAModule {
  if (reactGA) {
    return reactGA;
  }

  const imported = require('react-ga4');
  reactGA = (imported.default ?? imported) as ReactGAModule;
  return reactGA;
}

// Matches UUIDs - used for tree IDs, person IDs etc.
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
// Matches the token segment in /invite/:token
const INVITE_TOKEN_PATTERN = /(\/invite\/)[^/]+/i;

/**
 * Sanitizes a route pathname before sending it to GA.
 * - Replaces invite tokens with [token]
 * - Replaces UUIDs (tree/person IDs) with [id]
 * - Never includes query strings (caller must not pass location.search)
 */
export function sanitizePathForAnalytics(pathname: string): string {
  let sanitized = pathname.replace(INVITE_TOKEN_PATTERN, '$1[token]');
  sanitized = sanitized.replace(UUID_PATTERN, '[id]');
  return sanitized;
}

export function initializeAnalytics(): void {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID?.trim();
  const isEnabled = process.env.REACT_APP_GA_ENABLED !== 'false';

  if (!measurementId || !isEnabled) {
    return;
  }

  const isProduction = process.env.NODE_ENV === 'production';

  getReactGA().initialize(measurementId, {
    gaOptions: {
      // Localhost has no real domain - cookie_domain 'none' lets GA set the
      // cookie without a domain requirement. In production the default is used.
      ...(!isProduction && { cookieDomain: 'none' }),
    },
    gtagOptions: {
      // debug_mode sends events to GA4 DebugView so you can validate them
      // in real-time at: Analytics > Admin > DebugView
      ...(!isProduction && { debug_mode: true }),
    },
  });

  initialized = true;
}

export function trackPageView(path: string): void {
  if (!initialized) return;

  getReactGA().send({ hitType: 'pageview', page: path });
}

export function trackEvent(eventName: string, params?: Record<string, string | number | boolean>): void {
  if (!initialized) return;

  getReactGA().event(eventName, params);
}

export function isAnalyticsEnabled(): boolean {
  return initialized;
}
